import { type FC, useState } from 'react';
import { 
  AppBar, 
  Box, 
  Button, 
  Toolbar, 
  Typography, 
  Menu, 
  MenuItem, 
  IconButton, 
  Drawer, 
  List, 
  ListItem, 
  ListItemButton, 
  ListItemText, 
  Collapse,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { DASHBOARD_PATH } from '@routes/path';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faChevronDown, 
  faChevronUp,
  faBars, 
  faTimes 
} from '@fortawesome/free-solid-svg-icons';
import { LOGOUT_PATH } from '@routes/path';
import type { HeaderProps } from '@components/common/Header/interface';
import UserActions from '@components/common/Header/UserActions';

const Header: FC<HeaderProps> = ({
  items,
  userProfile
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // États pour le desktop
  const [anchorEls, setAnchorEls] = useState<{ [key: string]: HTMLElement | null }>({});
  
  // États pour le mobile
  const [mobileOpen, setMobileOpen] = useState(false);
  const [expandedMobileMenus, setExpandedMobileMenus] = useState<{ [key: string]: boolean }>({});

  // Gestionnaires de menu Desktop
  const handleMenuOpen = (itemLabel: string, event: React.MouseEvent<HTMLElement>) => {
    setAnchorEls((prev) => ({ ...prev, [itemLabel]: event.currentTarget }));
  };

  const handleMenuClose = (itemLabel: string) => {
    setAnchorEls((prev) => ({ ...prev, [itemLabel]: null }));
  };

  const handleMenuItemClick = (path: string) => {
    navigate(path);
    setAnchorEls({});
    setMobileOpen(false);
  };

  const handleLogout = () => {
    navigate(LOGOUT_PATH);
    setMobileOpen(false);
  };

  // Gestionnaires de menu Mobile
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMobileMenuToggle = (itemLabel: string) => {
    setExpandedMobileMenus((prev) => ({ ...prev, [itemLabel]: !prev[itemLabel] }));
  };

  const handleNavigate = () => {
    if (userProfile) {
      navigate(DASHBOARD_PATH);
    } else {
      window.location.assign(import.meta.env.VITE_JOB_OFFER_STATIC_DOMAIN);
    }
    
  };

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          backgroundColor: 'primary.main',
          borderRadius: 0,
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          zIndex: theme.zIndex.drawer + 1, // S'assure que l'AppBar reste au-dessus du Drawer si besoin
        }}
      >
        <Toolbar
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          {/* Logo */}
          <Box onClick={handleNavigate} sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <i className="fa-solid fa-utensils" style={{ fontSize: '18px', color: '#FFFFFF' }}></i>
            </Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                letterSpacing: '0.5px',
                color: '#FFFFFF',
              }}
            >
              Job Offer
            </Typography>
          </Box>

          {/* Version Desktop (reprend exactement votre logique) */}
          {!isMobile && (
            <>
              {/* Menu Items */}
              <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                {(items || []).map((item) => {
                  const hasChildren = item.children && item.children.length > 0;
                  const isOpen = Boolean(anchorEls[String(item.label)]);
                  
                  return (
                    <Box key={String(item.label)} sx={{ position: 'relative' }}>
                      <Button
                        onClick={(e) => {
                          if (hasChildren) {
                            handleMenuOpen(String(item.label), e);
                          } else {
                            navigate(item.path);
                          }
                        }}
                        sx={{
                          color: '#FFFFFF',
                          textTransform: 'none',
                          fontSize: '1rem',
                          fontWeight: 500,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                            borderRadius: '4px',
                          },
                        }}
                      >
                        <FontAwesomeIcon icon={item.icon} />
                        {item.label}
                        {hasChildren && (
                          <FontAwesomeIcon 
                            icon={faChevronDown} 
                            style={{ 
                              fontSize: '0.75rem',
                              transition: 'transform 0.2s',
                              transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)'
                            }}
                          />
                        )}
                      </Button>

                      {hasChildren && (
                        <Menu
                          anchorEl={anchorEls[String(item.label)]}
                          open={isOpen}
                          onClose={() => handleMenuClose(String(item.label))}
                          sx={{
                            '& .MuiPaper-root': {
                              backgroundColor: '#D05A0E',
                              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                            },
                          }}
                        >
                          {item.children!.map((child) => (
                            <MenuItem
                              key={child.path}
                              onClick={() => handleMenuItemClick(child.path)}
                              sx={{
                                color: '#FFFFFF',
                                '&:hover': {
                                  backgroundColor: 'rgba(255, 255, 255, 0.15)',
                                },
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <FontAwesomeIcon icon={child.icon} />
                                {child.label}
                              </Box>
                            </MenuItem>
                          ))}
                        </Menu>
                      )}
                    </Box>
                  );
                })}
              </Box>

              {/* Actions Utilisateur */}
              <UserActions
                userProfile={userProfile}
                onLogout={handleLogout}
              />
            </>
          )}

          {/* Bouton Hamburger pour Mobile */}
          {isMobile &&  (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ color: '#FFFFFF' }}
            >
              <FontAwesomeIcon icon={faBars} />
            </IconButton>
          )}
        </Toolbar>
      </AppBar>

      {/* Menu mobile latéral (Drawer) */}
      <Drawer
        variant="temporary"
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Meilleures performances sur mobile
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: 280,
            backgroundColor: 'primary.main',
            color: '#FFFFFF',
          },
        }}
      >
        {/* En-tête du Drawer avec bouton de fermeture */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2 }}>
          <IconButton onClick={handleDrawerToggle} sx={{ color: '#FFFFFF' }}>
            <FontAwesomeIcon icon={faTimes} />
          </IconButton>
        </Box>

        {/* Liste des menus dynamiques */}
        <List sx={{ px: 1 }}>
          {(items || []).map((item) => {
            const hasChildren = item.children && item.children.length > 0;
            const isExpanded = Boolean(expandedMobileMenus[String(item.label)]);

            return (
              <Box key={String(item.label)}>
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => {
                      if (hasChildren) {
                        handleMobileMenuToggle(String(item.label));
                      } else {
                        navigate(item.path);
                        setMobileOpen(false);
                      }
                    }}
                    sx={{
                      borderRadius: '4px',
                      mb: 0.5,
                      '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                      <FontAwesomeIcon icon={item.icon} style={{ width: '20px' }} />
                      <ListItemText primary={item.label} />
                      {hasChildren && (
                        <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} style={{ fontSize: '0.8rem' }} />
                      )}
                    </Box>
                  </ListItemButton>
                </ListItem>

                {/* Éléments enfants pliables */}
                {hasChildren && (
                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding sx={{ pl: 4 }}>
                      {item.children!.map((child) => (
                        <ListItem key={child.path} disablePadding>
                          <ListItemButton
                            onClick={() => handleMenuItemClick(child.path)}
                            sx={{
                              borderRadius: '4px',
                              mb: 0.5,
                              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.15)' }
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <FontAwesomeIcon icon={child.icon} style={{ width: '16px' }} />
                              <ListItemText primary={child.label} />
                            </Box>
                          </ListItemButton>
                        </ListItem>
                      ))}
                    </List>
                  </Collapse>
                )}
              </Box>
            );
          })}
        </List>

        
            <UserActions
              userProfile={userProfile}
              onLogout={handleLogout}
              onClose={() => setMobileOpen(false)}
              isMobile
            />
      </Drawer>
    </>
  )
};

export default Header;